import * as React from 'react';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {Typography} from '@mui/material';
import {fetchBypassExpensePendingReportCreator} from '@app/screens/module-specific-utilities/pages/bypass-expense/redux/slice';
import {appSelector} from '@app/store/selectors';
import {monthMock} from '@app/components/widgets/placeholder/multiselect-placeholder';
import {generateLastTenYears} from '@app/screens/module-specific-utilities/components/approve-expenses/utils/index';
import {CustomMenuItem} from '@app/screens/module-specific-utilities/components/approve-expenses/ApproveExpenseHeader';
import {useCallback, useState} from 'react';
import {MenuProp, useStyles} from '@app/screens/Styles/style';
import {approveExpenseStateSelector} from '@app/screens/module-specific-utilities/pages/approve-expenses/redux/selectors';
import Colors from '@app/themes/colors';
import {strings} from '@app/common/strings';

export const util: any = {
  handleOnClickGo: null,
  handleChangeDivision: null,
  handleChangeState: null,
  handleChangeYear: null,
  handleChangeMonth: null,
};

const selectedLabelStyle = {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  display: 'block',
};

export const ByPassExpenseHeader = ({
  onSearch,
}: {
  onSearch: (params: any) => void;
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [selectedDivisionIds, setSelectedDivisionIds] = useState<any[]>([]);
  const [selectedStateIds, setSelectedStateIds] = useState<any[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
  const [sapCode, setSapCode] = useState('');

  const divisionDropDown = useSelector(
    approveExpenseStateSelector?.getDivisionData(),
  );
  const stateDropDown = useSelector(
    approveExpenseStateSelector?.getStateData(),
  );
  const {isWriteAccess = false} = useSelector(
    appSelector.getUserInfo(),
    shallowEqual,
  );

  const lastTenYears: number[] = generateLastTenYears();
  const months = monthMock();

  const handleChangeDivision = useCallback((event: {target: {value: any}}) => {
    setSelectedDivisionIds(event.target.value);
  }, []);

  const stateOptions = (stateDropDown || []).filter(
    (stateObj: any) => stateObj.value !== 0,
  );

  const handleChangeState = useCallback((event: {target: {value: any}}) => {
    setSelectedStateIds(event.target.value);
  }, []);

  const handleChangeYear = useCallback((event: {target: {value: any}}) => {
    setSelectedYears(event.target.value);
  }, []);

  const handleChangeMonth = useCallback((event: {target: {value: any}}) => {
    setSelectedMonths(event.target.value);
  }, []);

  const handleOnClickGo = useCallback(() => {
    const stateNames = stateDropDown
      ?.filter((stateObj: any) => selectedStateIds.includes(stateObj.value))
      ?.map((stateObj: any) => stateObj.label);
    const params = {
      years: selectedYears.join(','),
      months: selectedMonths.join(','),
      sapCodes: sapCode.trim() ? sapCode.trim() : null,
      divisionIds: selectedDivisionIds.join(','),
      stateName: stateNames?.length ? stateNames.join(',') : null,
    };
    dispatch(fetchBypassExpensePendingReportCreator(params));
    onSearch(params);
  }, [
    selectedDivisionIds,
    selectedStateIds,
    stateDropDown,
    selectedYears,
    selectedMonths,
    sapCode,
    dispatch,
    onSearch,
  ]);

  const hasAnyFilter =
    selectedDivisionIds.length > 0 ||
    selectedStateIds.length > 0 ||
    selectedYears.length > 0 ||
    selectedMonths.length > 0 ||
    sapCode.trim() !== '';
  const validate = !hasAnyFilter;

  util.handleOnClickGo = handleOnClickGo;
  util.handleChangeDivision = handleChangeDivision;
  util.handleChangeState = handleChangeState;
  util.handleChangeYear = handleChangeYear;
  util.handleChangeMonth = handleChangeMonth;

  return (
    <Stack
      spacing={2}
      p={2}
      direction="row"
      alignItems="center"
      style={{
        backgroundColor: '#f4f4f4',
        height: 120,
        borderRadius: 6.7,
        margin: 5,
        overflowX: 'auto',
        overflowY: 'hidden',
        flexWrap: 'nowrap',
      }}>
      <Box sx={{width: 200, flexShrink: 0}}>
        <Typography className={classes.label}>
          {strings.ModuleSpecificUtilitie.bypassExpensePage.DIVISION}
        </Typography>
        <FormControl size="small" fullWidth>
          <InputLabel sx={{marginTop: 2.3, fontSize: 15}} id="division-dropdown">
            Select Division
          </InputLabel>
          <Select
            size="small"
            disabled={!isWriteAccess}
            multiple
            value={selectedDivisionIds}
            placeholder="Division"
            MenuProps={MenuProp}
            inputProps={{
              'data-testid': 'dropdown-setDivision',
            }}
            onChange={handleChangeDivision}
            label="Select Division"
            sx={{
              top: 18,
              backgroundColor: Colors?.white,
            }}
            renderValue={(selected: any) => (
              <span style={selectedLabelStyle}>
                {divisionDropDown
                  ?.filter((divisionObj: any) =>
                    selected.includes(divisionObj.value),
                  )
                  ?.map((divisionObj: any) => divisionObj.label)
                  .join(', ')}
              </span>
            )}>
            {divisionDropDown.map((divisionValue: any) => (
              <MenuItem key={divisionValue.value} value={divisionValue.value}>
                <CustomMenuItem
                  value={divisionValue.value}
                  checked={selectedDivisionIds.includes(divisionValue.value)}
                  label={divisionValue.label}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{width: 200, flexShrink: 0}}>
        <Typography className={classes.label}>
          {strings.ModuleSpecificUtilitie.bypassExpensePage.STATE}
        </Typography>
        <FormControl size="small" fullWidth>
          <InputLabel sx={{marginTop: 2.3, fontSize: 15}} id="state-dropdown">
            Select State
          </InputLabel>
          <Select
            size="small"
            disabled={!isWriteAccess}
            multiple
            value={selectedStateIds}
            placeholder="State"
            MenuProps={MenuProp}
            inputProps={{
              'data-testid': 'dropdown-setState',
            }}
            onChange={handleChangeState}
            label="Select State"
            sx={{
              top: 18,
              backgroundColor: Colors?.white,
            }}
            renderValue={(selected: any) => (
              <span style={selectedLabelStyle}>
                {stateOptions
                  ?.filter((stateObj: any) => selected.includes(stateObj.value))
                  ?.map((stateObj: any) => stateObj.label)
                  .join(', ')}
              </span>
            )}>
            {stateOptions.map((stateValue: any) => (
              <MenuItem key={stateValue.value} value={stateValue.value}>
                <CustomMenuItem
                  value={stateValue.value}
                  checked={selectedStateIds.includes(stateValue.value)}
                  label={stateValue.label}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{width: 120, flexShrink: 0}}>
        <Typography className={classes.label}>
          {strings.ModuleSpecificUtilitie.expense.YEAR}
        </Typography>
        <FormControl size="small" fullWidth>
          <InputLabel sx={{marginTop: 2.3, fontSize: 15}} id="year-dropdown">
            Exp Year
          </InputLabel>
          <Select
            inputProps={{
              'data-testid': 'dropdown-setYear',
            }}
            size="small"
            disabled={!isWriteAccess}
            multiple
            label="Exp Year"
            value={selectedYears}
            MenuProps={MenuProp}
            sx={{top: 17, backgroundColor: Colors?.white}}
            onChange={handleChangeYear}
            renderValue={(selected: any) => (
              <span style={selectedLabelStyle}>{selected.join(', ')}</span>
            )}>
            {lastTenYears.map((yearOption: number) => (
              <MenuItem key={yearOption} value={yearOption}>
                <CustomMenuItem
                  value={yearOption}
                  checked={selectedYears.includes(yearOption)}
                  label={yearOption}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{width: 150, flexShrink: 0}}>
        <Typography className={classes.label}>
          {strings.ModuleSpecificUtilitie.expense.MONTH}
        </Typography>
        <FormControl size="small" fullWidth>
          <InputLabel sx={{marginTop: 2.3, fontSize: 15}} id="month-dropdown">
            Exp Month
          </InputLabel>
          <Select
            inputProps={{
              'data-testid': 'dropdown-setMonth',
            }}
            disabled={!isWriteAccess}
            multiple
            value={selectedMonths}
            size="small"
            label="Exp Month"
            MenuProps={MenuProp}
            onChange={handleChangeMonth}
            sx={{
              top: 17,
              backgroundColor: Colors?.white,
            }}
            renderValue={(selected: any) => (
              <span style={selectedLabelStyle}>
                {months
                  .filter((monthVal: any) => selected.includes(monthVal.key))
                  .map((monthVal: any) => monthVal.value)
                  .join(', ')}
              </span>
            )}>
            {months.map((monthVal: any) => (
              <MenuItem key={monthVal.key} value={monthVal.key}>
                <CustomMenuItem
                  value={monthVal.key}
                  checked={selectedMonths.includes(monthVal.key)}
                  label={monthVal.value}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Box sx={{width: 150, flexShrink: 0}}>
        <Typography className={classes.label}>
          {strings.ModuleSpecificUtilitie.bypassExpensePage.SAP_CODE}
        </Typography>
        <TextField
          data-testid="input-setSapCode"
          size="small"
          fullWidth
          disabled={!isWriteAccess}
          placeholder="Enter SAP Code"
          value={sapCode}
          onChange={event => setSapCode(event.target.value)}
          sx={{top: 17, backgroundColor: Colors?.white}}
        />
      </Box>
      <Button
        data-testid="button-handleOnClickGo"
        variant="contained"
        disabled={validate}
        size="large"
        sx={{top: 25, width: 53.3, flexShrink: 0}}
        onClick={handleOnClickGo}>
        {strings.Go}
      </Button>
    </Stack>
  );
};
